export interface ServerPage {
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
  readonly currentPage: number;
  readonly totalPages: number;
}

export interface ResolveServerPaginationInput {
  readonly sourceData: unknown;
  /** Dot path on this source. Empty reads `pagination.total`. */
  readonly totalPath?: string;
  /** Page size chosen by the author. Offset is always 0 on load. */
  readonly limit?: number | string;
}

const WHOLE_NUMBER = /^(?:0|[1-9]\d*)$/;

const readPath = (root: unknown, path: string): unknown =>
  path.split('.').reduce<unknown>((current, key) => {
    if (current === null || typeof current !== 'object') return undefined;
    return (current as Record<string, unknown>)[key];
  }, root);

const wholeNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
    return value;
  }
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!WHOLE_NUMBER.test(trimmed)) return undefined;
  return Number(trimmed);
};

const readTotal = (
  sourceData: unknown,
  totalPath: string | undefined,
): number | undefined => {
  const authored = totalPath?.trim() ?? '';
  const path = authored.startsWith('$.') ? authored.slice(2) : authored;
  return wholeNumber(readPath(sourceData, path || 'pagination.total'));
};

const authoredLimit = (
  limit: number | string | undefined,
): number | undefined => {
  const value = wholeNumber(limit);
  if (value === undefined || value <= 0) return undefined;
  return value;
};

/**
 * Read the result total from this repeater's source. The page size is the
 * author's limit, and the loaded page is always the first one (offset 0).
 * A missing total or limit hides the footer.
 */
export const resolveServerPagination = ({
  sourceData,
  totalPath,
  limit,
}: ResolveServerPaginationInput): ServerPage | undefined => {
  const total = readTotal(sourceData, totalPath);
  const pageSize = authoredLimit(limit);
  if (total === undefined || pageSize === undefined) return undefined;

  return {
    total,
    limit: pageSize,
    offset: 0,
    currentPage: 1,
    totalPages: Math.ceil(total / pageSize),
  };
};

export interface PaginationFormKeys {
  readonly offset: string;
  readonly limit: string;
  readonly total: string;
  readonly currentPage: string;
  readonly totalPages: string;
}

/** One author-supplied prefix. Empty means the repeater does not write form state. */
export const paginationFormKeys = (
  formKey: string | undefined,
): PaginationFormKeys | undefined => {
  const prefix = formKey?.trim() ?? '';
  if (!prefix) return undefined;
  return {
    offset: `${prefix}Offset`,
    limit: `${prefix}Limit`,
    total: `${prefix}Total`,
    currentPage: `${prefix}CurrentPage`,
    totalPages: `${prefix}TotalPages`,
  };
};

export const paginationFormStateEntries = (
  formKey: string | undefined,
  page: Pick<
    ServerPage,
    'offset' | 'limit' | 'total' | 'currentPage' | 'totalPages'
  >,
): Record<string, string> | undefined => {
  const keys = paginationFormKeys(formKey);
  if (!keys) return undefined;
  return {
    [keys.offset]: String(page.offset),
    [keys.limit]: String(page.limit),
    [keys.total]: String(page.total),
    [keys.currentPage]: String(page.currentPage),
    [keys.totalPages]: String(page.totalPages),
  };
};

export const readResetFormKeys = (value: unknown): readonly string[] => {
  const raw = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  const keys = raw
    .filter((key): key is string => typeof key === 'string')
    .map((key) => key.trim())
    .filter((key) => key.length > 0);
  return Array.from(new Set(keys));
};
